import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export interface TaskAssigneeOption {
  user_id: string;
  name: string;
}

export function TaskAssigneePicker({
  options,
  value,
  onChange,
}: {
  options: TaskAssigneeOption[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const selected = new Set(value);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.name.toLowerCase().includes(needle));
  }, [options, query]);

  const toggle = (userId: string, checked: boolean) => {
    if (checked) onChange([...new Set([...value, userId])]);
    else onChange(value.filter((id) => id !== userId));
  };

  return (
    <div>
      <p className={cn(TYPE.label, 'mb-s2 text-ds-text')}>{t('tasks.field_assignees')}</p>
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('tasks.search_assignees')}
        aria-label={t('tasks.search_assignees')}
      />
      <div className="mt-s2 max-h-48 overflow-y-auto rounded-ctl border border-ds-line bg-ds-card">
        {filtered.length === 0 ? (
          <p className={cn(TYPE.small, 'px-s3 py-s3 text-ds-muted')}>{t('tasks.no_assignees')}</p>
        ) : (
          <ul>
            {filtered.map((option) => (
              <li key={option.user_id} className="border-b border-ds-line last:border-b-0">
                <Checkbox
                  className="px-s3 py-s2"
                  checked={selected.has(option.user_id)}
                  onChange={(event) => toggle(option.user_id, event.target.checked)}
                  label={option.name}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
